import { Box, IconButton, MenuItem, Select, Typography } from "@mui/material";
import {
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
} from "@mui/icons-material";
import PropTypes from "prop-types";

export const PAGE_DIRECTION = {
  FIRST: "FIRST",
  PREVIOUS: "PREVIOUS",
  NEXT: "NEXT",
  LAST: "LAST",
};

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const DEFAULT_ROWS_PER_PAGE = 20;

/**
 * @description Pagination for cursor based APIs (no total count needed).
 * Navigation is driven by hasPrev / hasNext flags instead of page count.
 */
const CursorPagination = ({
  page = 0,
  rowsPerPage = DEFAULT_ROWS_PER_PAGE,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  currentCount = 0,
  totalCount,
  hasPrev = false,
  hasNext = false,
  isFirstLastPageEnable = false,
  isRowPerPageEnable = true,
  disabled = false,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const from = currentCount ? page * rowsPerPage + 1 : 0;
  const to = page * rowsPerPage + currentCount;
  const hasTotal = typeof totalCount === "number";

  const handlePageChange = (event, direction) =>
    onPageChange?.(event, direction);

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      flexWrap="wrap"
      gap={6}
      px={2}
      py={1}
      data-testid="cursor-pagination"
    >
      {isRowPerPageEnable && (
        <Box display="flex" alignItems="center" gap={4}>
          <Typography variant="body2">Rows per page:</Typography>
          <Select
            variant="standard"
            disableUnderline
            value={rowsPerPage}
            disabled={disabled}
            onChange={(event) =>
              onRowsPerPageChange?.(event, Number(event.target.value))
            }
            inputProps={{ "data-testid": "rows-per-page" }}
            sx={{ fontSize: 14 }}
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Typography variant="body2" data-testid="page-range">
        {`${from}–${to}`}
        {hasTotal ? ` of ${totalCount}` : ""}
      </Typography>

      <Box display="flex" alignItems="center">
        {isFirstLastPageEnable && (
          <IconButton
            onClick={(event) => handlePageChange(event, PAGE_DIRECTION.FIRST)}
            disabled={disabled || !hasPrev}
            aria-label="first page"
            data-testid="first-page"
          >
            <FirstPage />
          </IconButton>
        )}
        <IconButton
          onClick={(event) => handlePageChange(event, PAGE_DIRECTION.PREVIOUS)}
          disabled={disabled || !hasPrev}
          aria-label="previous page"
          data-testid="previous-button"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(event) => handlePageChange(event, PAGE_DIRECTION.NEXT)}
          disabled={disabled || !hasNext}
          aria-label="next page"
          data-testid="next-button"
        >
          <KeyboardArrowRight />
        </IconButton>
        {isFirstLastPageEnable && (
          <IconButton
            onClick={(event) => handlePageChange(event, PAGE_DIRECTION.LAST)}
            disabled={disabled || !hasNext}
            aria-label="last page"
            data-testid="last-page"
          >
            <LastPage />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

CursorPagination.propTypes = {
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  currentCount: PropTypes.number,
  totalCount: PropTypes.number,
  hasPrev: PropTypes.bool,
  hasNext: PropTypes.bool,
  isFirstLastPageEnable: PropTypes.bool,
  isRowPerPageEnable: PropTypes.bool,
  disabled: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func,
};

export default CursorPagination;
